/**
 * `<kit-scope-nav>` styles — one stylesheet, two worlds.
 *
 * The component ships NO palette of its own. Every colour resolves through a
 * chain of the platform's own token names, then the shadcn/Tailwind names the
 * React apps use, then a system colour that follows `color-scheme`:
 *
 *   --ink → --foreground → CanvasText
 *   --raised → --popover → Canvas
 *   --line → --border → a mix of the resolved ink
 *
 * That is why the same element survives the career light/warm palette and the
 * DNA dark one without a `theme` prop: whichever host is in charge has already
 * defined its tokens, and the system colours cover the bare case in BOTH
 * schemes. A host that wants to override one thing overrides one custom
 * property on `kit-scope-nav`, not a class.
 *
 * Structure over boxes (platform design default, career UX §0): the nav is
 * hairlines, typography and spacing. The dropdown is the ONE surface — it has
 * to be, to float above content — and even there the entries are FLUSH rows
 * separated by hairlines, never rounded pills inside a padded box.
 *
 * ── The height contract (this is what broke on a phone) ──────────────────────
 * The nav's intrinsic height is two SHORT lines: the app eyebrow plus the
 * breadcrumb row. Together they fit the 3rem app bar every platform app uses.
 * A host must therefore NEVER give `.scope-crumb` a `min-height` to chase a
 * touch target: two stacked 44px crumbs cannot fit a 48px bar, the lockup
 * overflows, and a centred overflow puts the eyebrow ABOVE the top of the bar,
 * where it is clipped. The touch target is solved here instead — the crumb
 * stretches to the full height of the breadcrumb row and pads sideways, and the
 * row's own height is the knob (`--scope-touch`), so growing the target grows
 * the BAR rather than bleeding out of it.
 */

import { SPACE_ACCENTS } from "../brand/spaces";

export const SCOPE_NAV_STYLES_CSS = /* css */ `
kit-scope-nav {
  /* ---- knobs: colour (host tokens win; system colours close the chain) ---- */
  --scope-ink: var(--ink, var(--foreground, CanvasText));
  --scope-ink-soft: var(--ink-2, var(--muted-foreground, color-mix(in oklab, var(--scope-ink) 66%, transparent)));
  --scope-ink-faint: color-mix(in oklab, var(--scope-ink) 38%, transparent);
  --scope-line: var(--line, var(--border, color-mix(in oklab, var(--scope-ink) 18%, transparent)));
  --scope-surface: var(--raised, var(--popover, Canvas));
  --scope-hover: color-mix(in oklab, var(--scope-ink) 8%, transparent);
  --scope-shadow: 0 1px 2px color-mix(in oklab, var(--scope-ink) 10%, transparent),
                  0 18px 42px -20px color-mix(in oklab, var(--scope-ink) 46%, transparent);

  /* the five spaces — the ONE feature that changes with the top scope */
  --scope-space-personal: ${SPACE_ACCENTS.personal};
  --scope-space-business: ${SPACE_ACCENTS.business};
  --scope-space-family: ${SPACE_ACCENTS.family};
  --scope-space-community: ${SPACE_ACCENTS.community};
  --scope-space-public: ${SPACE_ACCENTS.public};
  --scope-accent: var(--scope-ink);

  /* ---- knobs: metrics ---------------------------------------------------- */
  --scope-mark-size: 2rem;
  --scope-radius: var(--radius-sm, 8px);
  --scope-gap: 0.6rem;
  --scope-font: var(--sans, ui-sans-serif, system-ui, sans-serif);
  --scope-font-app: var(--scope-font);
  --scope-separator: "\\203A";       /* › — DNA sets "/" */
  /* The breadcrumb row's height IS the pointer target: 24px, the WCAG 2.5.8 (AA)
     minimum. It is a knob, and it is deliberately NOT the 44px of 2.5.5 (AAA) —
     the eyebrow plus a 44px row cannot fit a 3rem app bar, and a target that
     bleeds out of the bar is not a target, it is a clipped nav. A host with a
     taller bar may raise it. */
  --scope-touch: 1.5rem;
  /* How wide a single crumb may get before it ellipses. */
  --scope-crumb-max: 15rem;

  display: flex;
  align-items: center;
  gap: var(--scope-gap);
  min-width: 0;
  max-width: 100%;
  font-family: var(--scope-font);
  color: var(--scope-ink);
}

kit-scope-nav[space="personal"]  { --scope-accent: var(--scope-space-personal); }
kit-scope-nav[space="business"]  { --scope-accent: var(--scope-space-business); }
kit-scope-nav[space="family"]    { --scope-accent: var(--scope-space-family); }
kit-scope-nav[space="community"] { --scope-accent: var(--scope-space-community); }
kit-scope-nav[space="public"]    { --scope-accent: var(--scope-space-public); }

/* ---- the mark ----------------------------------------------------------- */
kit-scope-nav .scope-home {
  display: inline-flex;
  align-items: center;
  flex: 0 0 auto;
  color: inherit;
  text-decoration: none;
  border-radius: var(--scope-radius);
}
kit-scope-nav .scope-brand {
  /* the mark follows the host's ink and the nav's height — nothing else */
  --brand-size: var(--scope-mark-size);
  --brand-ink: var(--scope-ink);
  flex: 0 0 auto;
}
kit-scope-nav .scope-mark-svg {
  height: var(--scope-mark-size);
  width: auto;
  display: block;
  fill: currentColor;
}

/* the hairline between mark and lockup — the whole "divider" idea, one pixel */
kit-scope-nav .scope-rule {
  flex: 0 0 auto;
  inline-size: 1px;
  block-size: calc(var(--scope-mark-size) * 0.72);
  background: var(--scope-rule-color, var(--scope-accent));
  opacity: var(--scope-rule-opacity, 0.55);
}

/* ---- the lockup: app eyebrow ABOVE the breadcrumb ------------------------ */
kit-scope-nav .scope-lockup {
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  justify-content: center;
  min-width: 0;
  flex: 0 1 auto;
}
kit-scope-nav .scope-line {
  display: flex;
  align-items: center;
  min-width: 0;
  max-width: 100%;
}
/* the eyebrow — the application, not a crumb: small, quiet, never interactive
   chrome you have to hit */
kit-scope-nav .scope-line-app {
  line-height: 1.05;
  padding-block-end: 0.06rem;
}
kit-scope-nav .scope-line-app .scope-crumb {
  font-family: var(--scope-font-app);
  font-size: 0.64rem;
  font-weight: 600;
  letter-spacing: 0.11em;
  text-transform: uppercase;
  color: var(--scope-ink-soft);
}

/* ---- the breadcrumb ----------------------------------------------------- */
/* One line, section first, one separator between neighbours. It may scroll
   sideways on a narrow bar — nothing is ever hidden from the eye OR from the
   accessibility tree, and the page itself never scrolls because the whole
   lockup can shrink (min-width: 0 all the way down). */
kit-scope-nav .scope-path {
  min-width: 0;
  max-width: 100%;
}
kit-scope-nav .scope-crumbs {
  display: flex;
  align-items: stretch;
  gap: 0;
  margin: 0;
  padding: 0;
  list-style: none;
  min-width: 0;
  max-width: 100%;
  overflow-x: auto;
  overflow-y: hidden;
  overscroll-behavior-inline: contain;
  scrollbar-width: none;
  scroll-snap-type: inline proximity;
}
kit-scope-nav .scope-crumbs::-webkit-scrollbar { display: none; }
/* the edge that continues past the bar fades out — a cut-off crumb must read as
   »there is more this way«, never as a rendering fault (the element sets
   data-overflow after every render and on every scroll) */
kit-scope-nav .scope-crumbs[data-overflow="start"] { mask-image: linear-gradient(to right, transparent 0, #000 1.6rem); }
kit-scope-nav .scope-crumbs[data-overflow="end"] { mask-image: linear-gradient(to left, transparent 0, #000 1.6rem); }
kit-scope-nav .scope-crumbs[data-overflow="both"] {
  mask-image: linear-gradient(to right, transparent 0, #000 1.6rem, #000 calc(100% - 1.6rem), transparent 100%);
}
/* A crumb never SHRINKS — a breadcrumb squeezed to »W. › Me… › Werte« reads as
   nothing at all. It keeps its natural width (ellipsing only a genuinely long
   single label) and the row scrolls instead, anchored at the leaf. */
kit-scope-nav .scope-crumb-item {
  display: flex;
  align-items: stretch;
  flex: 0 0 auto;
}
kit-scope-nav .scope-crumb-item.is-leaf { scroll-snap-align: end; }

/* one segment: static text, or the trigger of its siblings */
kit-scope-nav .scope-crumb {
  display: inline-flex;
  align-items: center;
  gap: 0.22rem;
  min-width: 0;
  max-width: var(--scope-crumb-max);
  font: inherit;
  font-size: 0.9rem;
  line-height: 1.2;
  color: var(--scope-ink-soft);
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}
/* The pointer target belongs to the BREADCRUMB row only — the eyebrow is a label,
   not something you aim at, and giving it a target height is what made the lockup
   too tall for the bar in the first place. */
kit-scope-nav .scope-crumbs .scope-crumb { min-block-size: var(--scope-touch); }
kit-scope-nav .scope-crumb-static {
  padding-inline: 0.1rem;
}
kit-scope-nav button.scope-crumb {
  background: none;
  border: 0;
  border-radius: 0;
  padding: 0 0.34rem;
  cursor: pointer;
  transition: color 120ms ease, box-shadow 120ms ease;
}
/* Hover/open: the TEXT lights up and a hairline underlines the trigger — the
   platform's "no bounding box" mechanic. Never a filled pill in the bar. */
kit-scope-nav button.scope-crumb:hover,
kit-scope-nav button.scope-crumb[aria-expanded="true"] {
  color: var(--scope-ink);
  box-shadow: inset 0 -1px 0 var(--scope-accent);
}
kit-scope-nav .scope-crumb:focus-visible,
kit-scope-nav .scope-home:focus-visible,
kit-scope-nav .scope-choice:focus-visible {
  outline: 2px solid var(--scope-accent);
  outline-offset: 2px;
}
kit-scope-nav .scope-crumb-text {
  min-width: 0;
  overflow: hidden;
  text-overflow: ellipsis;
}
/* the deepest crumb is the one you are actually in (an explicit class, not
   :last-of-type — crumbs are spans OR buttons, so type counting lies) */
kit-scope-nav .scope-crumb-leaf {
  color: var(--scope-ink);
  font-weight: 600;
}
kit-scope-nav .scope-caret {
  inline-size: 0.72em;
  block-size: 0.72em;
  flex: 0 0 auto;
  fill: none;
  stroke: currentColor;
  stroke-width: 2.4;
  stroke-linecap: round;
  stroke-linejoin: round;
  opacity: 0.6;
  transition: transform 140ms ease;
}
kit-scope-nav button.scope-crumb[aria-expanded="true"] .scope-caret {
  transform: rotate(180deg);
  opacity: 1;
}

kit-scope-nav .scope-sep {
  flex: 0 0 auto;
  display: inline-flex;
  align-items: center;
  color: var(--scope-ink-faint);
  font-size: 0.82em;
  padding-inline: 0.12rem;
}
kit-scope-nav .scope-sep::before { content: var(--scope-separator); }

/* ---- the dropdown ------------------------------------------------------- */
/* A floating surface, because it must float — but inside it there are no boxes:
   full-bleed rows, hairline separators, no radius, no padding gutter that would
   make the entries read as stacked buttons (career UX §0). */
kit-scope-nav .scope-menu {
  position: fixed;
  inset: auto;
  margin: 0;
  padding: 0;
  min-inline-size: 12rem;
  max-inline-size: min(22rem, calc(100vw - 1.5rem));
  max-block-size: min(24rem, calc(100vh - 5rem));
  overflow-y: auto;
  border: 1px solid var(--scope-line);
  border-radius: var(--scope-radius);
  background: var(--scope-surface);
  color: var(--scope-ink);
  box-shadow: var(--scope-shadow);
  font-family: var(--scope-font);
}
kit-scope-nav .scope-menu:not(:popover-open) { display: none; }

@supports (anchor-name: --a) {
  kit-scope-nav .scope-menu {
    top: calc(anchor(bottom) + 0.4rem);
    left: anchor(left);
    position-try-fallbacks: flip-block, flip-inline;
  }
}

kit-scope-nav .scope-choice {
  display: flex;
  flex-direction: column;
  gap: 0.1rem;
  inline-size: 100%;
  text-align: start;
  font: inherit;
  font-size: 0.88rem;
  color: var(--scope-ink-soft);
  background: none;
  border: 0;
  border-radius: 0;
  padding: 0.55rem 0.85rem;
  text-decoration: none;
  cursor: pointer;
  transition: background-color 110ms ease, color 110ms ease;
}
/* the hairline between neighbours — the list reads as a document, not as tiles */
kit-scope-nav .scope-choice + .scope-choice,
kit-scope-nav .scope-choice + .scope-menu-group,
kit-scope-nav .scope-menu-group + .scope-choice {
  border-block-start: 1px solid color-mix(in oklab, var(--scope-line) 60%, transparent);
}
kit-scope-nav .scope-choice:hover { background: var(--scope-hover); color: var(--scope-ink); }
kit-scope-nav .scope-choice.is-current {
  color: var(--scope-ink);
  font-weight: 600;
  box-shadow: inset 2px 0 0 var(--scope-accent);
}
kit-scope-nav .scope-choice-hint {
  font-size: 0.78rem;
  font-weight: 400;
  color: var(--scope-ink-faint);
}
kit-scope-nav .scope-menu-group {
  display: block;
  padding: 0.7rem 0.85rem 0.3rem;
  font-size: 0.66rem;
  font-weight: 600;
  letter-spacing: 0.1em;
  text-transform: uppercase;
  color: var(--scope-ink-faint);
}

/* ---- narrow ------------------------------------------------------------- */
/* Three triggers, one rule set: the viewport is narrow, the nav's own container
   is narrow (a header beside a collapsible sidebar declares
   \`container: scope / inline-size\`), or the host says so with \`compact\`.
   Nothing is hidden — the mark shrinks and the crumbs give up width first. */
${narrow("@media (max-width: 48rem)")}
${narrow("@container scope (max-width: 30rem)")}
${narrow(null)}

@media (prefers-reduced-motion: reduce) {
  kit-scope-nav .scope-caret,
  kit-scope-nav button.scope-crumb,
  kit-scope-nav .scope-choice { transition: none; }
}
`;

/** The narrow rule set, emitted once per trigger (viewport · container · attribute). */
function narrow(at: string | null): string {
  const host = at === null ? "kit-scope-nav[compact]" : "kit-scope-nav";
  const rules = /* css */ `
  ${host} { --scope-mark-size: 1.7rem; --scope-crumb-max: 9rem; }
  ${host} .scope-crumb { font-size: 0.86rem; }`;
  return at === null ? rules : `${at} {${rules}\n}`;
}

const INSTALLED = new WeakSet<Document>();

/** Install the scope-nav stylesheet once per document (idempotent). */
export function installScopeNavStyles(doc: Document = document): void {
  if (INSTALLED.has(doc)) return;
  INSTALLED.add(doc);
  const constructable =
    typeof CSSStyleSheet !== "undefined" &&
    "adoptedStyleSheets" in doc &&
    typeof CSSStyleSheet.prototype.replaceSync === "function";
  if (constructable) {
    try {
      const sheet = new CSSStyleSheet();
      sheet.replaceSync(SCOPE_NAV_STYLES_CSS);
      doc.adoptedStyleSheets = [...doc.adoptedStyleSheets, sheet];
      return;
    } catch {
      /* fall through to the <style> tag */
    }
  }
  if (doc.querySelector("style[data-kit-scope-nav]")) return;
  const style = doc.createElement("style");
  style.setAttribute("data-kit-scope-nav", "");
  style.textContent = SCOPE_NAV_STYLES_CSS;
  doc.head.append(style);
}
